'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import Image from 'next/image';
import { AiOutlineMail, AiOutlineUnlock } from 'react-icons/ai';
import { loginUser } from '../../lib/loginUser'; // Your login logic

const LoginForm = () => {
  const router = useRouter();
  const isLt = false;
  const rawRedirect = Array.isArray(router.query.redirect)
    ? router.query.redirect[0]
    : router.query.redirect;
  const safeRedirect =
    typeof rawRedirect === 'string' && /^\/(?!\/)/.test(rawRedirect)
      ? rawRedirect
      : '';
  const redirectQuery = safeRedirect
    ? `?redirect=${encodeURIComponent(safeRedirect)}`
    : '';
  const signupHref = (isLt ? '/lt/registracija' : '/signup') + redirectQuery;
  const homeHref = isLt ? '/lt/' : '/';
  const resetHref = (isLt ? '/lt/atkurti-slaptazodi' : '/reset-password') + redirectQuery;

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitError('');

    const cleanedEmail = email.trim();
    const cleanedPassword = password.trim();

    if (!/\S+@\S+\.\S+/.test(cleanedEmail)) {
      setSubmitError(isLt ? 'Neteisingas el. pašto formatas' : 'Invalid email format');
      return;
    }
    if (cleanedPassword.length < 6) {
      setSubmitError(
        isLt ? 'Slaptažodis turi būti bent 6 simbolių' : 'Password must be at least 6 characters'
      );
      return;
    }

    try {
      setLoading(true);
      const res = await loginUser(cleanedEmail, cleanedPassword);

      if (!res.ok) {
        // Handle specific error codes with user-friendly messages
        const errorCode = res.errorCode || '';
        let errorMessage = '';

        if (
          errorCode === 'auth/invalid-credential' ||
          errorCode === 'auth/wrong-password' ||
          errorCode === 'auth/user-not-found' ||
          errorCode === 'invalid_login_credentials'
        ) {
          errorMessage = isLt
            ? 'Neteisingi prisijungimo duomenys arba nepatvirtintas el. paštas'
            : 'Incorrect login credentials or unverified email';
        } else if (errorCode === 'auth/email-not-verified') {
          errorMessage = isLt
            ? 'Prašome patvirtinti savo el. paštą prieš prisijungiant'
            : 'Please verify your email before logging in';
        } else if (errorCode === 'auth/too-many-requests') {
          errorMessage = isLt
            ? 'Per daug bandymų. Bandykite vėliau.'
            : 'Too many attempts. Please try again later.';
        } else {
          errorMessage = res.error || (isLt ? 'Prisijungti nepavyko' : 'Login failed');
        }

        setSubmitError(errorMessage);
      } else {
        router.replace(safeRedirect || (isLt ? '/lt/' : '/'));
      }
    } catch (err: any) {
      setSubmitError(err?.message || (isLt ? 'Nenumatyta klaida' : 'Unexpected error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className='flex items-start justify-center w-full px-4 pt-10'>
      <div className='w-full max-w-md bg-white/95 dark:bg-black border border-gray-200 dark:border-gray-800 rounded-xl px-6 pt-4 pb-6'>
        <Link href={homeHref} className='mb-8 mt-6 flex justify-center'>
          <Image src='/GadgetFever/GadgetFever_webp_logo.webp' alt='GadgetFever' width={220} height={70} className='object-contain' />
        </Link>

        <h1 className='text-2xl font-semibold text-center mt-1 mb-3 text-gray-900 dark:text-white'>
          {isLt ? 'Prisijungti' : 'Sign In'}
        </h1>

        <form className='space-y-4' onSubmit={handleSubmit}>
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>{isLt ? 'El. paštas' : 'Email'}</label>
            <div className='flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black focus-within:ring-1 focus-within:ring-gray-600'>
              <AiOutlineMail className='text-gray-500 dark:text-gray-400 flex-shrink-0 ml-3' />
              <input
                type='email'
                name='email'
                placeholder={isLt ? 'pvz@email.com' : 'you@example.com'}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className='flex-1 min-w-0 py-3 pr-4 bg-transparent text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none'
              />
            </div>
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'>{isLt ? 'Slaptažodis' : 'Password'}</label>
            <div className='flex items-center gap-2 rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-black focus-within:ring-1 focus-within:ring-gray-600'>
              <AiOutlineUnlock className='text-gray-500 dark:text-gray-400 flex-shrink-0 ml-3' />
              <input
                type='password'
                name='password'
                placeholder='••••••••'
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className='flex-1 min-w-0 py-3 pr-4 bg-transparent text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none'
              />
            </div>
          </div>

          <button
            type='submit'
            disabled={loading}
            className='w-full bg-black hover:bg-gray-900 dark:hover:bg-[#090909] disabled:opacity-60 text-white font-semibold py-3 rounded-md transition-colors border border-gray-700'
          >
            {loading ? (isLt ? 'Vykdoma...' : 'Processing...') : isLt ? 'Prisijungti' : 'Sign In'}
          </button>

          <Link
            href={homeHref}
            className='block w-full text-center py-2.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors'
          >
            {isLt ? '← Į pradžios puslapį' : '← Back to home'}
          </Link>

          {submitError && (
            <p className='text-red-400 text-sm text-center'>{submitError}</p>
          )}

          <p className='text-sm text-center text-gray-600 dark:text-gray-400 mt-2'>
            {isLt ? 'Neturite paskyros?' : "Don't have an account?"}{' '}
            <Link
              href={signupHref}
              className='text-gray-900 dark:text-white hover:underline'
            >
              {isLt ? 'Registruokitės' : 'Register'}
            </Link>
          </p>
          <p className='text-sm text-center text-gray-600 dark:text-gray-400 mt-2'>
            {isLt ? 'Pamiršote slaptažodį?' : 'Forgot password?'}{' '}
            <Link
              href={resetHref}
              className='text-gray-900 dark:text-white hover:underline'
            >
              {isLt ? 'Atkurti' : 'Reset'}
            </Link>
          </p>

        </form>
      </div>
    </section>
  );
};

export default LoginForm;
